import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try {
        const { data: b } = await api.get("/billing/status");
        setBilling(b);
      } catch (err) {
        console.warn("billing/status failed:", err?.message || err);
        setBilling(null);
      }
    } catch (err) {
      // 401 on /auth/me is expected when not logged in — swallow, but log others
      if (err?.response?.status && err.response.status !== 401) {
        console.error("checkAuth failed:", err);
      }
      setUser(null);
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth, skip /me — AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("brimi_token", data.token);
    setUser(data.user);
    try { const { data: b } = await api.get("/billing/status"); setBilling(b); }
    catch (err) { console.warn("billing/status after login failed:", err?.message || err); }
    return data.user;
  };

  const signupEmail = async (name, email, password, role) => {
    const { data } = await api.post("/auth/signup", { name, email, password, role });
    localStorage.setItem("brimi_token", data.token);
    setUser(data.user);
    try { const { data: b } = await api.get("/billing/status"); setBilling(b); }
    catch (err) { console.warn("billing/status after signup failed:", err?.message || err); }
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("logout endpoint failed (proceeding anyway):", err?.message || err); }
    localStorage.removeItem("brimi_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, loginEmail, signupEmail, logout, billing, setBilling }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
